"use client";

import { c as _c } from "react/compiler-runtime";
import { useNextMintDrop } from "@/hooks/useNextMintDrop";
import { useNowMintingStatus } from "@/hooks/useNowMintingStatus";
import { useWaveDropsLeaderboard, WaveDropsLeaderboardSort } from "@/hooks/useWaveDropsLeaderboard";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import { ManifoldClaimStatus } from "@/hooks/useManifoldClaim";
import { shouldShowNextWinnerInComingUp } from "@/helpers/mint-visibility.helpers";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import Link from "next/link";
import { LeadingCard } from "./LeadingCard";
import { NextMintCard } from "./NextMintCard";
const SKELETON_KEYS = ["coming-up-skeleton-1", "coming-up-skeleton-2", "coming-up-skeleton-3"];
export function NextMintLeadingSection() {
  const $ = _c(47);
  const {
    nft: nowMinting,
    isFetching: isNowMintingFetching,
    status: nowMintingStatus
  } = useNowMintingStatus();
  const {
    nextMint,
    nextMintTitle,
    waveId,
    isReady,
    isFetching: isWinnersFetching
  } = useNextMintDrop();
  const t0 = waveId ?? "";
  const t1 = !waveId;
  let t2;
  if ($[0] !== t0 || $[1] !== t1) {
    t2 = {
      waveId: t0,
      sort: WaveDropsLeaderboardSort.RATING_PREDICTION,
      pausePolling: t1
    };
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    drops,
    isFetching: isLeaderboardFetching
  } = useWaveDropsLeaderboard(t2);
  let t3;
  if ($[3] !== nextMintTitle || $[4] !== nowMinting) {
    t3 = !!nowMinting?.name && !!nextMintTitle && nowMinting.name.toLowerCase().trim() === nextMintTitle.toLowerCase().trim();
    $[3] = nextMintTitle;
    $[4] = nowMinting;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const isNextMintSameAsNowMinting = t3;
  const t4 = nowMintingStatus === ManifoldClaimStatus.ENDED;
  const t5 = !!nextMint;
  let t6;
  if ($[6] !== t4 || $[7] !== t5) {
    t6 = shouldShowNextWinnerInComingUp({
      isMintEnded: t4,
      nextMintExists: t5
    });
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const canShowNextMint = t6;
  const showNextMint = canShowNextMint && !!nextMint && !isNextMintSameAsNowMinting;
  const leadingCount = showNextMint ? 2 : 3;
  let t10;
  let t11;
  let t12;
  let t7;
  let t8;
  let t9;
  if ($[9] !== drops || $[10] !== isLeaderboardFetching || $[11] !== isNowMintingFetching || $[12] !== isReady || $[13] !== isWinnersFetching || $[14] !== leadingCount || $[15] !== nextMint || $[16] !== showNextMint || $[17] !== waveId) {
    t12 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const leading = drops.slice(0, leadingCount);
      const isFetching = isNowMintingFetching || isWinnersFetching || isLeaderboardFetching;
      if (!isReady || !waveId) {
        t12 = null;
        break bb0;
      }
      let t13;
      if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
        t13 = <div><span className="tw-m-0 tw-text-xl tw-font-semibold tw-tracking-tight tw-text-iron-200 md:tw-text-2xl">Coming up</span><p className="tw-mb-0 tw-mt-2 tw-text-base tw-text-iron-500">Next in queue and current vote leaders</p></div>;
        $[24] = t13;
      } else {
        t13 = $[24];
      }
      let t14;
      if ($[25] !== waveId) {
        t14 = getWaveRoute({
          waveId,
          isDirectMessage: false,
          isApp: false
        });
        $[25] = waveId;
        $[26] = t14;
      } else {
        t14 = $[26];
      }
      let t15;
      let t16;
      if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
        t15 = <span>View all</span>;
        t16 = <ArrowRightIcon className="tw-size-4 tw-flex-shrink-0" aria-hidden={true} />;
        $[27] = t15;
        $[28] = t16;
      } else {
        t15 = $[27];
        t16 = $[28];
      }
      let t17;
      if ($[29] !== t14) {
        t17 = <div className="tw-mb-8 tw-flex tw-flex-col tw-items-start tw-gap-4 sm:tw-flex-row sm:tw-items-end sm:tw-justify-between">{t13}<Link href={t14} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-medium tw-text-iron-400 tw-no-underline tw-transition-colors hover:tw-text-white">{t15}{t16}</Link></div>;
        $[29] = t14;
        $[30] = t17;
      } else {
        t17 = $[30];
      }
      const header = t17;
      if (isFetching && !showNextMint && leading.length === 0) {
        let t18;
        if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
          t18 = <div className="tw-grid tw-grid-cols-1 tw-gap-6 lg:tw-grid-cols-3 lg:tw-gap-8">{SKELETON_KEYS.map(_temp)}</div>;
          $[31] = t18;
        } else {
          t18 = $[31];
        }
        let t19;
        if ($[32] !== header) {
          t19 = <section className="tw-px-4 md:tw-px-6 lg:tw-px-8 tw-relative tw-z-10 tw-@container tw-pb-10 md:tw-pb-16"><div>{header}{t18}</div></section>;
          $[32] = header;
          $[33] = t19;
        } else {
          t19 = $[33];
        }
        t12 = t19;
        break bb0;
      }
      if (!showNextMint && leading.length === 0) {
        t12 = null;
        break bb0;
      }
      t11 = "tw-px-4 md:tw-px-6 lg:tw-px-8 tw-relative tw-z-10 tw-@container tw-pb-10 md:tw-pb-16";
      t10 = header;
      t7 = "tw-grid tw-grid-cols-1 tw-gap-6 lg:tw-grid-cols-3 lg:tw-gap-8";
      if ($[34] !== nextMint || $[35] !== showNextMint) {
        t8 = showNextMint && <NextMintCard drop={nextMint} />;
        $[34] = nextMint;
        $[35] = showNextMint;
        $[36] = t8;
      } else {
        t8 = $[36];
      }
      t9 = leading.map(_temp2);
    }
    $[9] = drops;
    $[10] = isLeaderboardFetching;
    $[11] = isNowMintingFetching;
    $[12] = isReady;
    $[13] = isWinnersFetching;
    $[14] = leadingCount;
    $[15] = nextMint;
    $[16] = showNextMint;
    $[17] = waveId;
    $[18] = t10;
    $[19] = t11;
    $[20] = t12;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    t10 = $[18];
    t11 = $[19];
    t12 = $[20];
    t7 = $[21];
    t8 = $[22];
    t9 = $[23];
  }
  if (t12 !== Symbol.for("react.early_return_sentinel")) {
    return t12;
  }
  let t13;
  if ($[37] !== t7 || $[38] !== t8 || $[39] !== t9) {
    t13 = <div className={t7}>{t8}{t9}</div>;
    $[37] = t7;
    $[38] = t8;
    $[39] = t9;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== t10 || $[42] !== t13) {
    t14 = <div>{t10}{t13}</div>;
    $[41] = t10;
    $[42] = t13;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  let t15;
  if ($[44] !== t11 || $[45] !== t14) {
    t15 = <section className={t11}>{t14}</section>;
    $[44] = t11;
    $[45] = t14;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  return t15;
}
function _temp2(drop, index) {
  return <LeadingCard key={drop.id} drop={drop} rank={index + 1} />;
}
function _temp(key) {
  return <NextMintLeadingSkeletonCard key={key} />;
}
const NextMintLeadingSkeletonCard = () => {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-border-b tw-border-white/10 tw-bg-iron-900 tw-px-2.5 tw-py-1.5 sm:tw-px-3 sm:tw-py-2"><div className="tw-h-4 tw-w-24 tw-animate-pulse tw-rounded tw-bg-iron-800/60" /><div className="tw-h-3 tw-w-16 tw-animate-pulse tw-rounded tw-bg-iron-800/60" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="tw-relative tw-flex tw-aspect-[3/4] tw-max-h-[clamp(320px,70vw,500px)] tw-w-full tw-items-center tw-justify-center tw-overflow-hidden tw-bg-iron-950"><div className="tw-h-full tw-w-full tw-animate-pulse tw-rounded-lg tw-bg-iron-900/70" /></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="tw-flex tw-items-center tw-gap-2"><div className="tw-h-4 tw-w-10 tw-animate-pulse tw-rounded tw-bg-iron-800/60" /><div className="tw-h-4 tw-flex-1 tw-animate-pulse tw-rounded tw-bg-iron-800/60" /></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="tw-flex tw-flex-col tw-text-left"><div className="tw-flex tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-white/10 tw-bg-iron-950">{t0}{t1}<div className="tw-flex tw-flex-col tw-gap-3 tw-border-t tw-border-white/10 tw-px-3 tw-py-3 sm:tw-px-4">{t2}<div className="tw-flex tw-items-center tw-gap-2"><div className="tw-size-6 tw-animate-pulse tw-rounded-full tw-bg-iron-800/60" /><div className="tw-h-3 tw-w-24 tw-animate-pulse tw-rounded tw-bg-iron-800/60" /></div></div></div></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};